import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import "./mathjax.scss";

declare global {
  interface Window {
    MathJax: any;
  }
}

interface MathJaxComponentProps {
  content: string;
  is_Solution?: boolean;
  variant?: 'chat' | 'exercise';
}


export const replaceMarkdown = (text: string, is_Solution: boolean) => {
  // Protect HTML table blocks from Markdown and line-break parsing
  const tablePlaceholders: string[] = [];
  text = text.replace(/(<table[\s\S]*?<\/table>)/gi, (match) => {
    // Sanitize any invalid <br> tags between table structural tags if present
    const cleanTable = match.replace(
      /(<\/?(?:table|thead|tbody|tfoot|tr)[^>]*>)\s*<br\s*\/?>|<br\s*\/?>\s*(<\/?(?:table|thead|tbody|tfoot|tr|th|td)[^>]*>)/gi,
      '$1$2'
    );
    tablePlaceholders.push(cleanTable);
    return `__TABLE_BLOCK_PLACEHOLDER_${tablePlaceholders.length - 1}__`;
  });

  // Protect all MathJax math blocks from Markdown and list-item parsing
  const mathPlaceholders: string[] = [];
  text = text.replace(/(\$\$[\s\S]*?\$\$|\\\[[\s\S]*?\\\]|\$[\s\S]*?\$|\\\([\s\S]*?\\\))/g, (match) => {
    mathPlaceholders.push(match);
    return `__MATH_BLOCK_PLACEHOLDER_${mathPlaceholders.length - 1}__`;
  });

  // Auto-wrap raw unwrapped LaTeX environments (e.g. \begin{cases}...\end{cases}) in $$...$$ if omitted by AI
  text = text.replace(/((?:[a-zA-Z0-9_\{\}\(\)\\+\-=\*\cdot\s]+=\s*)?\\begin\{(cases|aligned|matrix|array|gathered|align\*?|equation\*?)\}[\s\S]*?\\end\{\2\})/g, (match) => {
    if (match.includes('__MATH_BLOCK_PLACEHOLDER_')) return match;
    mathPlaceholders.push(`$$${match.trim()}$$`);
    return `__MATH_BLOCK_PLACEHOLDER_${mathPlaceholders.length - 1}__`;
  });

  // Replace \left[ and \right] with \Big[ and \Big] to allow line breaks within brackets
  text = text.replace(/\\left\[/g, '\\Big[').replace(/\\right\]/g, '\\Big]');
  text = text.replace(/\\left\(/g, '\\Big(').replace(/\\right\)/g, '\\Big)');

  text = text.replace(/&#?(\d+);/g, (match, dec) => {
    return String.fromCharCode(dec);
  });

  // Decode common HTML entities before any replacements
  text = text.replace(/&quot;/g, '"')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&amp;/g, '&');

  // Replace **text** with <strong>text</strong>
  text = text.replace(/---/g, '');
  text = text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');

  // Replace markdown links [label](url) with HTML anchor tags
  text = text.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2">$1</a>');



  // For cases where the AI placed a header on the SAME line as other text:
  // Identify bold headers as any strong tag containing a colon.
  const headerPattern = '(<strong>[^<]*[:：][^<]*<\\/strong>|<strong>[^<]+<\\/strong>\\s*[:：])';

  text = text.replace(new RegExp(`([^>\\n])\\s*${headerPattern}`, 'g'), (match, p1, p2, offset, str) => {
    const isListMarker = p1 === '.' && offset > 0 && /[\\dא-ת]/.test(str[offset - 1]);
    if (isListMarker) return match;
    return p1 + '<br/>' + p2;
  });

  text = text.replace(new RegExp(`${headerPattern}\\s*([^\\s<-])`, 'g'), (match, p1, p2, offset, str) => {
    const beforeMatch = str.substring(Math.max(0, offset - 10), offset);
    if (/[\\dא-ת]\\.\\s*$/.test(beforeMatch)) return match;
    return p1 + '<br/>' + p2;
  });

  // Replace - or * or • or + text with <li>text</li> and wrap with <ul>
  text = text.replace(/^\s*[-*•+]\s*(.*)$/gm, '<li>$1</li>');
  text = text.replace(/(<li>[\s\S]*?<\/li>(?:\s*<li>[\s\S]*?<\/li>)*)/g, (match) => {
    return `<ul>${match.replace(/\n/g, '')}</ul>`;
  });

  // Add line break before numbered items (1., 2., etc.)
  text = text.replace(/(\s)(\d+\.)/g, '$1<br/>$2');

  // Add line break before Hebrew alphabetical sub-questions (א., ב., ג., etc.)
  text = text.replace(/([.?!:;]\s+)([א-ת]\.)(\s+)/g, '$1<br/>$2$3');

  const getVisualLength = (str: string) => {
    // Remove LaTeX commands (e.g. \Big, \cdot, \alpha, \frac, \left, \right, \bar)
    // and layout/script characters like spaces, curly braces, backslashes, _ and ^
    return str.replace(/\\[a-zA-Z]+/g, '').replace(/[\s{}\\_^]/g, '').length;
  };

  /**
   * Splits long fraction numerators into multi-line stacked fractions.
   * e.g. \frac{k(k+1)(2k+1) + 6(k+1)^2}{6} becomes a fraction with a two-line numerator.
   */
  const splitLongFractions = (tex: string): string => {
    let result = '';
    let i = 0;

    while (i < tex.length) {
      // Look for \frac
      if (tex.substring(i, i + 5) === '\\frac') {
        let j = i + 5;
        while (j < tex.length && tex[j] === ' ') j++;

        if (j < tex.length && tex[j] === '{') {
          // Extract numerator with brace matching
          let numStart = j + 1;
          let depth = 1;
          j++;
          while (j < tex.length && depth > 0) {
            if (tex[j] === '{') depth++;
            else if (tex[j] === '}') depth--;
            j++;
          }
          let numerator = tex.substring(numStart, j - 1);

          while (j < tex.length && tex[j] === ' ') j++;

          if (j < tex.length && tex[j] === '{') {
            // Extract denominator with brace matching
            let denStart = j + 1;
            depth = 1;
            j++;
            while (j < tex.length && depth > 0) {
              if (tex[j] === '{') depth++;
              else if (tex[j] === '}') depth--;
              j++;
            }
            let denominator = tex.substring(denStart, j - 1);

            // Only split if the visual numerator is genuinely long (> 26 visual chars) and not already stacked
            const numVisualLen = getVisualLength(numerator);
            if (numVisualLen > 26 && !numerator.includes('\\begin{gathered}')) {
              // Find a + or - at brace/paren depth 0 closest to the visual midpoint
              let bestSplitPos = -1;
              let bestDiff = Infinity;
              const targetVisualMid = numVisualLen / 2;
              let bd = 0;
              let pd = 0;
              for (let k = 0; k < numerator.length; k++) {
                if (numerator[k] === '{') bd++;
                else if (numerator[k] === '}') bd--;
                else if (numerator[k] === '(' || numerator[k] === '[') pd++;
                else if (numerator[k] === ')' || numerator[k] === ']') pd--;

                if (bd === 0 && pd === 0 && (numerator[k] === '+' || numerator[k] === '-') && k > 3) {
                  const leftVis = getVisualLength(numerator.substring(0, k));
                  const rightVis = getVisualLength(numerator.substring(k));
                  if (leftVis >= 6 && rightVis >= 6) {
                    const diff = Math.abs(leftVis - targetVisualMid);
                    if (diff < bestDiff) {
                      bestDiff = diff;
                      bestSplitPos = k;
                    }
                  }
                }
              }

              if (bestSplitPos >= 0) {
                let part1 = numerator.substring(0, bestSplitPos).trim();
                let part2 = numerator.substring(bestSplitPos).trim();
                result += `\\frac{\\begin{gathered} ${part1} \\\\ ${part2} \\end{gathered}}{${denominator}}`;
                i = j;
                continue;
              }
            }

            // No split needed, output as-is
            result += tex.substring(i, j);
            i = j;
            continue;
          }
        }
      }

      result += tex[i];
      i++;
    }

    return result;
  };

  /**
   * Splits long lines within an existing \begin{aligned} environment.
   * Walks through the aligned content character by character, tracking depth,
   * and inserts line breaks at clean positions inside [...] brackets.
   */
  const splitLongAlignedLines = (tex: string): string => {
    const startTag = '\\begin{aligned}';
    const endTag = '\\end{aligned}';

    let result = '';
    let currentIdx = 0;

    while (true) {
      let startIdx = tex.indexOf(startTag, currentIdx);
      if (startIdx === -1) {
        result += tex.substring(currentIdx);
        break;
      }

      let endIdx = tex.indexOf(endTag, startIdx);
      if (endIdx === -1) {
        result += tex.substring(currentIdx);
        break;
      }

      // Append content before \begin{aligned}
      result += tex.substring(currentIdx, startIdx + startTag.length);

      let content = tex.substring(startIdx + startTag.length, endIdx);
      let braceDepth = 0;
      let parenDepth = 0;
      let bracketDepth = 0;
      let charsSinceBreak = 0;
      let processedContent = '';

      for (let i = 0; i < content.length; i++) {
        let c = content[i];

        // Detect existing LaTeX line breaks (\\) and reset line counters
        if (c === '\\' && i + 1 < content.length && content[i + 1] === '\\') {
          processedContent += '\\\\';
          i++; // skip second backslash
          charsSinceBreak = 0;
          braceDepth = 0;
          parenDepth = 0;
          bracketDepth = 0;
          continue;
        }

        if (c === '{') braceDepth++;
        else if (c === '}') braceDepth--;
        else if (c === '(') parenDepth++;
        else if (c === ')') parenDepth--;
        else if (c === '[') bracketDepth++;
        else if (c === ']') bracketDepth--;

        charsSinceBreak++;

        let charsLeft = content.length - i;
        let isSafeBreak = braceDepth === 0 && parenDepth === 0;

        // Split if line is long enough (> 25 characters) and not near the end
        if (isSafeBreak && charsSinceBreak > 25 && charsLeft > 10) {
          if (c === '=') {
            processedContent += '\\\\\n& ' + c;
            charsSinceBreak = 0;
            continue;
          } else if (c === '+' || c === '-') {
            processedContent += '\\\\\n&\\quad ' + c;
            charsSinceBreak = 0;
            continue;
          }
        }

        processedContent += c;
      }

      result += processedContent;
      result += endTag;
      currentIdx = endIdx + endTag.length;
    }

    return result;
  };

  const formatLongMath = (math: string) => {
    let isDoubleDollar = math.startsWith('$$');
    let isBracket = math.startsWith('\\[');
    let isSingleDollar = !isDoubleDollar && math.startsWith('$');
    let isParen = math.startsWith('\\(');

    let inner = math;
    if (isDoubleDollar) inner = math.substring(2, math.length - 2);
    else if (isBracket) inner = math.substring(2, math.length - 2);
    else if (isSingleDollar) inner = math.substring(1, math.length - 1);
    else if (isParen) inner = math.substring(2, math.length - 2);
    else return math;

    if (inner.includes('\\begin{gathered}') ||
      inner.includes('\\begin{cases}') ||
      inner.includes('\\begin{matrix}') ||
      inner.includes('\\begin{array}') ||
      inner.includes('\\begin{align}')) return math;

    // Pre-process: split long fraction numerators (works on ANY content, including aligned)
    let originalInner = inner;
    inner = splitLongFractions(inner);

    // If content has \begin{aligned}, apply line splitting and return
    if (inner.includes('\\begin{aligned}')) {
      inner = splitLongAlignedLines(inner);
      // Return modified content if anything changed
      if (inner !== originalInner) {
        if (isDoubleDollar) return `$$${inner}$$`;
        if (isBracket) return `\\[${inner}\\]`;
        if (isSingleDollar) return `$${inner}$`;
        if (isParen) return `\\(${inner}\\)`;
      }
      return math;
    }

    // Only process if the whole equation is visually long enough to need wrapping
    const visualLengthThreshold = 26;
    if (getVisualLength(inner) < visualLengthThreshold) {
      if (inner !== originalInner) {
        if (isDoubleDollar) return `$$${inner}$$`;
        if (isBracket) return `\\[${inner}\\]`;
        if (isSingleDollar) return `$${inner}$`;
        if (isParen) return `\\(${inner}\\)`;
      }
      return math;
    }

    let braceDepth = 0;
    let parenDepth = 0;   // tracks ( and )
    let bracketDepth = 0; // tracks [ and ]
    let leftRightDepth = 0;
    let result = "";
    let modified = false;
    let charsSinceLastBreak = 0;
    let lastBreakSrcIndex = 0;

    for (let i = 0; i < inner.length; i++) {
      let char = inner[i];

      // Detect manual LaTeX line breaks (\\\\) and reset the counter
      if (char === '\\' && i + 1 < inner.length && inner[i + 1] === '\\') {
        charsSinceLastBreak = 0;
        lastBreakSrcIndex = i + 2;
        result += '\\\\';
        i++; // skip second backslash
        continue;
      }

      if (char === '{') braceDepth++;
      if (char === '}') braceDepth--;
      if (char === '(') parenDepth++;
      if (char === ')') parenDepth--;
      if (char === '[') bracketDepth++;
      if (char === ']') bracketDepth--;

      if (inner.substr(i, 5) === '\\left') leftRightDepth++;
      if (inner.substr(i, 6) === '\\right') leftRightDepth--;

      // Safest break: completely outside everything
      let isSafeBreak = braceDepth === 0 && leftRightDepth === 0 && parenDepth === 0 && bracketDepth === 0;

      // Clean bracket break: inside [...] but NOT inside (...) or {} or \left\right
      const breakDistanceThreshold = 15;
      let isCleanBracketBreak = braceDepth === 0 && leftRightDepth === 0 && parenDepth === 0 && bracketDepth > 0 && charsSinceLastBreak > breakDistanceThreshold;

      // Deep emergency break: inside nested (...) within [...], last resort
      let isDeepEmergencyBreak = braceDepth === 0 && leftRightDepth === 0 && parenDepth > 0 && bracketDepth > 0 && charsSinceLastBreak > breakDistanceThreshold;

      let charsLeft = inner.length - i;
      let shouldBreak = false;

      // Do not insert line breaks if we are close to the end of the equation
      const charsLeftThreshold = 8;
      const safeBreakDistanceThreshold = 12;

      const remainingStr = inner.substring(i);
      const remainingVisualLength = getVisualLength(remainingStr);
      const segmentSinceLastBreakVisualLength = getVisualLength(inner.substring(lastBreakSrcIndex, i));

      if (charsLeft > charsLeftThreshold && remainingVisualLength > 8) {
        if (isSafeBreak) {
          // Only break at equals (=) if the preceding segment is visually substantial (> 6 visual chars, not a short LHS like \bar{x} or f(x))
          if (char === '=' && segmentSinceLastBreakVisualLength > 6) shouldBreak = true;
          if ((char === '+' || char === '-') && segmentSinceLastBreakVisualLength > safeBreakDistanceThreshold && charsSinceLastBreak > (safeBreakDistanceThreshold + 5)) shouldBreak = true;
        } else if (isCleanBracketBreak) {
          if ((char === '=' || char === '+' || char === '-') && segmentSinceLastBreakVisualLength > 10 && charsSinceLastBreak > breakDistanceThreshold) shouldBreak = true;
        } else if (isDeepEmergencyBreak) {
          if ((char === '=' || char === '+' || char === '-') && segmentSinceLastBreakVisualLength > 10 && charsSinceLastBreak > breakDistanceThreshold) shouldBreak = true;
        }
      }

      if (shouldBreak && i > 0 && i < inner.length - 1) {
        result += ' \\\\ \n';
        if (char === '=') {
          result += '& ';
        } else {
          result += '&\\quad ';
        }
        result += char;
        modified = true;
        charsSinceLastBreak = 0;
        lastBreakSrcIndex = i + 1;
      } else {
        result += char;
        charsSinceLastBreak++;
      }
    }

    if (modified) {
      let finalInner = result.trim();
      if (!finalInner.startsWith('&')) {
        finalInner = '& ' + finalInner;
      }
      const aligned = `\\begin{aligned}\n${finalInner}\n\\end{aligned}`;
      // Force all stacked math into block display mode so it gets the white bubble CSS!
      if (isBracket || isParen) return `\\[${aligned}\\]`;
      return `$$${aligned}$$`;
    }
    return math;
  };

  // Restore all protected math blocks before splitting tags and formatting math
  text = text.replace(/__MATH_BLOCK_PLACEHOLDER_(\d+)__/g, (match, index) => {
    return mathPlaceholders[parseInt(index, 10)];
  });

  // Split by <style> blocks first to keep them and their CSS content completely intact
  const styleParts = text.split(/(<style[\s\S]*?<\/style>)/gi);
  text = styleParts.map((stylePart, styleIndex) => {
    if (styleIndex % 2 !== 0) return stylePart; // Keep <style>...</style> completely intact

    // Split by HTML tags first to keep them completely intact
    const tagParts = stylePart.split(/(<\/?(?:strong|b|i|em|a|br|li|ul|ol|div|sup|span|p|img|video|source|iframe|audio|table|thead|tbody|tr|th|td|h1|h2|h3|h4|h5|h6|hr|code|pre|html|body|head|meta|link)\b(?:\s+[a-zA-Z:-]+(?:=(?:"[^"]*"|'[^']*'|[^\s'">]+))?)*\s*\/?>)/gi);
    return tagParts.map((tagPart, tagIndex) => {
      if (tagIndex % 2 !== 0) return tagPart; // Keep HTML tags intact

      // Escape < and > inside text content
      const escapedTagPart = tagPart.replace(/</g, '&lt;').replace(/>/g, '&gt;');

      // Split text content by MathJax math blocks
      const mathParts = escapedTagPart.split(/(\$\$[\s\S]*?\$\$|\\\[[\s\S]*?\\\]|\$[\s\S]*?\$|\\\([\s\S]*?\\\))/g);
      return mathParts.map((mathPart, mathIndex) => {
        if (mathIndex % 2 !== 0) {
          // 1. Temporarily protect existing \text{}, \mbox{}, \textnormal{} blocks
          const textCmdPlaceholders: string[] = [];
          let processedMath = mathPart.replace(/\\(text|mbox|textnormal)\{([^{}]+)\}/g, (match) => {
            textCmdPlaceholders.push(match);
            return `__TEXT_CMD_${textCmdPlaceholders.length - 1}__`;
          });

          // 2. Auto-wrap any remaining raw unwrapped Hebrew inside math blocks with \text{}
          processedMath = processedMath.replace(/([\u0590-\u05FF\u05BE]+(?:[-\s][\u0590-\u05FF\u05BE]+)*)/g, '\\text{$1}');

          // 3. Restore protected \text{} blocks
          processedMath = processedMath.replace(/__TEXT_CMD_(\d+)__/g, (match, index) => {
            return textCmdPlaceholders[parseInt(index, 10)];
          });

          // Reverse Hebrew text inside \text{}, \mbox{}, or \textnormal{} for MathJax SVG LTR layout
          let cleanMath = processedMath.replace(/\\(text|mbox|textnormal)\{([^{}]+)\}/g, (match, cmd, content) => {
            if (/[\u0590-\u05FF]/.test(content)) {
              const words = content.split(' ');
              const reversedWords = words.map(word => {
                if (/[\u0590-\u05FF]/.test(word)) {
                  return word.split('').reverse().join('');
                }
                return word;
              }).reverse();
              return `\\${cmd}{${reversedWords.join(' ')}}`;
            }
            return match;
          });

          // Intercept and format ALL long math, including inline $...$
          // The formatLongMath function will safely ignore short inline math anyway
          return formatLongMath(cleanMath);
        }
        return mathPart.replace(/\n/g, '<br/>');
      }).join('');
    }).join('');
  }).join('');

  // Restore all protected table blocks
  text = text.replace(/__TABLE_BLOCK_PLACEHOLDER_(\d+)__/g, (match, index) => {
    return tablePlaceholders[parseInt(index, 10)];
  });

  if (is_Solution) {
    // Fix the the Derivative of a function display
    text = text.replace(/e\^x'/g, '(e^x)\'');
    text = text.replace(/\(([^)]+)\)'/g, '($1)\'');

    // Fix LaTeX display math split across any number of p tags
    text = text.replace(/<p>\\\[<\/p>([\s\S]*?)<p>\\\]<\/p>/g, (match, content) => {
      // Remove all p tags and join content with proper line breaks
      const cleanMath = content
        .replace(/<\/p>\s*<p>/g, ' \\\\ ')  // Replace </p><p> with LaTeX line break
        .replace(/<\/?p>/g, '')             // Remove remaining p tags
        .trim();
      return `<div class="math-display-solution">\\[${cleanMath}\\]</div>`;
    });
    // Fix $$ display math split across p tags
    text = text.replace(/<p>\$<\/p>([\s\S]*?)<p>\$<\/p>/g, (match, content) => {
      const cleanMath = content
        .replace(/<\/p>\s*<p>/g, ' \\\\ ')
        .replace(/<\/?p>/g, '')
        .trim();
      return `<div class="math-display-solution">$${cleanMath}$</div>`;
    });


    // Handle standalone LaTeX display math blocks (must come first)
    text = text.replace(/\\\[\s*([\s\S]*?)\s*\\\]/g, (match, content) => {
      return `\\[${content.trim()}\\]`;
    });


    // Fix inline math with Hebrew spans
    text = text.replace(/\\\(([\s\S]*?)\\\)/g, (match, mathContent) => {
      return `\\(${mathContent}\\)`;
    });

    // Fix consecutive superscripts by grouping them properly
    //text = text.replace(/\^([^{}\s]+)\^([^{}\s]+)/g, '^{$1^{$2}}');

    // Fix HTML superscripts to LaTeX
    text = text.replace(/<sup>([^<]*)<\/sup>/gi, '^{$1}');
    text = text.replace(/<\/sup>/gi, '');

    // Fix fractions with HTML remnants
    text = text.replace(/\\frac\{([^{}]*)\}\{([^{}<>]*)<\/sup>([^{}]*)\}/g, '\\frac{$1}{$2^$3}');

    // Fix parentheses with superscripts
    text = text.replace(/\(([^()<>\n]*)<sup>([^<>()\n]*)\)/g, '($1^$2)');

    // Clean Hebrew spans with multiline support
    text = text.replace(/<span dir="rtl">([\s\S]*?)<\/span>/g, '$1');
  }

  return text;
};

const MathJaxComponent = ({ content, is_Solution = false, variant = 'chat' }: MathJaxComponentProps) => {
  const navigate = useNavigate();
  const [text, setText] = useState('');
  const divRef = React.useRef<HTMLDivElement>(null);

  useEffect(() => {
    const formattedcontent = replaceMarkdown(content, is_Solution);
    setText(formattedcontent);
  }, [content, is_Solution]);

  useEffect(() => {
    let isMounted = true;
    let timeoutId: any = null;

    if (window.MathJax && divRef.current) {
      timeoutId = setTimeout(() => {
        if (!isMounted || !divRef.current) return;

        try {
          // Check if the element is still in the document before processing
          if (document.contains(divRef.current)) {
            window.MathJax.typesetClear([divRef.current]);
            window.MathJax.typesetPromise([divRef.current]).catch((err: any) => {
              console.error("MathJax typesetPromise error:", err);
            });
          }
        } catch (err) {
          console.error("MathJax sync error:", err);
        }
      }, 50);
    }

    return () => {
      isMounted = false;
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [text]);

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;
    const anchor = target.closest("a");
    if (anchor && anchor.getAttribute("href")?.startsWith("/")) {
      e.stopPropagation();
      e.preventDefault();
      const route = anchor.getAttribute("href")!;
      navigate(route);
    }
  };

  return (
    <div
      ref={divRef}
      className={`mathjax-container mathjax-variant-${variant}`}
      onClick={handleClick}
      dangerouslySetInnerHTML={{ __html: text }}
    />
  );
};

export default MathJaxComponent;
